/**
 * Simple test arrow that points at the camera
 */

import type * as THREE_NS from 'three';

export function createTestArrow(
  scene: THREE_NS.Scene,
  THREE: typeof THREE_NS
): THREE_NS.Group {
  const { Group } = THREE as any;
  
  // Create an empty group at origin - invisible but can parent objects
  const emptyGroup = new Group();
  emptyGroup.position.set(0, 0, 0);
  scene.add(emptyGroup);
  
  return emptyGroup;
}

export function updateTestArrow(
  arrow: THREE_NS.Group,
  camera: THREE_NS.Camera,
  THREE: typeof THREE_NS
): void {
  if (!arrow || !camera) return;
  
  const { Vector3, Quaternion } = THREE as any;
  
  // Get camera position
  const cameraPosition = new Vector3();
  camera.getWorldPosition(cameraPosition);
  
  // Calculate direction from arrow to camera
  const direction = new Vector3()
    .subVectors(cameraPosition, arrow.position)
    .normalize();
  
  // Project to XZ plane for horizontal rotation only (Y-axis rotation)
  const horizontalDirection = new Vector3(
    direction.x,
    0,
    direction.z
  );
  
  // Check if camera is directly above or below
  const horizontalLength = horizontalDirection.length();
  if (horizontalLength < 0.001) {
    return; // Keep current rotation
  }
  
  horizontalDirection.normalize();
  
  // Calculate Y-axis rotation angle directly to avoid quaternion wrapping issues
  // atan2(z, x) gives angle from +X axis (0° = +X, 90° = +Z, 180° = -X, 270° = -Z)
  // Invert the angle to reverse rotation direction
  const yawAngle = -Math.atan2(horizontalDirection.z, horizontalDirection.x);
  
  // Create pure Y-axis quaternion (no pitch/roll, avoids wrapping issues)
  const quaternion = new Quaternion();
  quaternion.setFromAxisAngle(new Vector3(0, 1, 0), yawAngle);
  
  // Rotate 90 degrees around Z-axis
  const offsetQuaternion = new Quaternion();
  offsetQuaternion.setFromAxisAngle(new Vector3(0, 0, 1), Math.PI / 2); // 90 degrees
  quaternion.multiplyQuaternions(quaternion, offsetQuaternion);
  
  // Flip 180 degrees around X-axis to face camera instead of pointing away
  const flipQuaternion = new Quaternion();
  flipQuaternion.setFromAxisAngle(new Vector3(1, 0, 0), Math.PI); // 180 degrees around X-axis
  quaternion.multiplyQuaternions(quaternion, flipQuaternion);
  
  // Apply rotation to arrow (pure Y-axis rotation prevents tilting and wrapping issues)
  arrow.quaternion.copy(quaternion);
}

